import { type CalendarDisplayEvent } from "@/pages/CalendarPage"
import { useCurrency } from "@/context/currency/currency-context"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"

const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
	paid: { bg: "bg-green-100", text: "text-green-700 dark:text-green-400", label: "Paid" },
	declared: { bg: "bg-[#0ea5e9]/10", text: "text-[#0ea5e9] dark:text-sky-400", label: "Declared" },
	predicted: { bg: "bg-sky-100 dark:bg-sky-900/30", text: "text-sky-700 dark:text-sky-200", label: "Predicted" },
	earnings: { bg: "bg-indigo-500/10", text: "text-indigo-600 dark:text-indigo-300", label: "Earnings" },
}

function formatRevenue(value: number | null | undefined): string {
	if (value == null) return "—"
	if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`
	if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`
	return `$${value.toLocaleString()}`
}

function earningsTimingLabel(time?: string): string {
	switch (time) {
		case "amc":
			return "After Market Close"
		case "bmo":
			return "Before Market Open"
		default:
			return ""
	}
}

interface DayDetailDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	date: Date | null
	events: CalendarDisplayEvent[]
}

export function DayDetailDialog({ open, onOpenChange, date, events }: DayDetailDialogProps) {
	const { format, convert } = useCurrency()

	if (!date) return null

	const dateLabel = date.toLocaleDateString("en-US", {
		weekday: "long",
		month: "long",
		day: "numeric",
		year: "numeric",
	})

	const paymentEvents = events.filter((e) => e.date_type === "payment")
	const exDateEvents = events.filter((e) => e.date_type === "ex-date")
	const earningsEvents = events.filter((e) => e.event_type === "earnings")
	const paymentTotal = paymentEvents.reduce((sum, e) => sum + convert(e.amount, e.currency), 0)

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="text-sky-950 dark:text-sky-50">{dateLabel}</DialogTitle>
					<DialogDescription>
						{paymentEvents.length > 0 && (
							<>
								{paymentEvents.length} payment{paymentEvents.length !== 1 ? "s" : ""} &middot;{" "}
								{format(paymentTotal, { skipConversion: true })}
							</>
						)}
						{paymentEvents.length > 0 && exDateEvents.length > 0 && " · "}
						{exDateEvents.length > 0 && (
							<>
								{exDateEvents.length} ex-date{exDateEvents.length !== 1 ? "s" : ""}
							</>
						)}
						{(paymentEvents.length > 0 || exDateEvents.length > 0) && earningsEvents.length > 0 && " · "}
						{earningsEvents.length > 0 && (
							<>
								{earningsEvents.length} earnings report{earningsEvents.length !== 1 ? "s" : ""}
							</>
						)}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-2 max-h-[60vh] overflow-y-auto">
					{paymentEvents.map((event, idx) => {
						const style = STATUS_COLORS[event.status] || STATUS_COLORS.predicted
						return (
							<div
								key={`pay-${event.ticker}-${idx}`}
								className="flex items-center justify-between p-3 rounded-lg bg-white/60 dark:bg-black/20 border border-sky-900/10 dark:border-sky-100/10"
							>
								<div className="flex items-center gap-3">
									<div className="font-bold text-sky-950 dark:text-sky-50 text-sm">
										{event.ticker}
									</div>
									<span
										className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${style.bg} ${style.text}`}
									>
										{style.label}
									</span>
									<span className="text-[10px] text-muted-foreground">Payment</span>
								</div>
								<div className="text-right">
									<div className="font-semibold text-sm text-sky-950 dark:text-sky-50">
										{format(event.amount, { fromCurrency: event.currency })}
									</div>
									{event.portfolio_name && (
										<div className="text-[10px] text-muted-foreground">{event.portfolio_name}</div>
									)}
								</div>
							</div>
						)
					})}

					{exDateEvents.map((event, idx) => {
						const style = STATUS_COLORS[event.status] || STATUS_COLORS.predicted
						return (
							<div
								key={`ex-${event.ticker}-${idx}`}
								className="flex items-center justify-between p-3 rounded-lg bg-white/60 dark:bg-black/20 border border-dashed border-sky-600/30 dark:border-sky-400/30"
							>
								<div className="flex items-center gap-3">
									<div className="font-bold text-sky-950 dark:text-sky-50 text-sm">
										{event.ticker}
									</div>
									<span
										className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${style.bg} ${style.text}`}
									>
										{style.label}
									</span>
									<span className="text-[10px] font-medium text-sky-700 dark:text-sky-300">
										Ex-Date
									</span>
								</div>
								<div className="text-right">
									<div className="font-semibold text-sm text-sky-950 dark:text-sky-50">
										{format(event.amount, { fromCurrency: event.currency })}
									</div>
									{event.portfolio_name && (
										<div className="text-[10px] text-muted-foreground">{event.portfolio_name}</div>
									)}
								</div>
							</div>
						)
					})}

					{earningsEvents.map((event, idx) => {
						const style = STATUS_COLORS.earnings
						const hasBeat = event.eps_actual != null && event.eps_estimate != null
						const beat = hasBeat ? event.eps_actual! > event.eps_estimate! : null

						return (
							<div
								key={`earn-${event.ticker}-${idx}`}
								className="p-3 rounded-lg bg-white/60 dark:bg-black/20 border border-indigo-500/20 dark:border-indigo-400/20"
							>
								<div className="flex items-center justify-between mb-2">
									<div className="flex items-center gap-3">
										<div className="font-bold text-sky-950 dark:text-sky-50 text-sm">
											{event.ticker}
										</div>
										<span
											className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${style.bg} ${style.text}`}
										>
											{event.is_upcoming ? "Upcoming" : "Reported"}
										</span>
										{event.earnings_time && (
											<span className="text-[10px] text-muted-foreground">
												{earningsTimingLabel(event.earnings_time)}
											</span>
										)}
									</div>
									{hasBeat && (
										<span
											className={`text-xs font-bold ${beat ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}
										>
											{beat ? "Beat" : "Miss"}
										</span>
									)}
								</div>
								{event.company_name && (
									<div className="text-[11px] text-muted-foreground mb-1.5">{event.company_name}</div>
								)}
								<div className="grid grid-cols-2 gap-3 text-xs">
									<div>
										<span className="text-muted-foreground">EPS Est: </span>
										<span className="font-medium text-sky-950 dark:text-sky-50">
											{event.eps_estimate != null ? `$${event.eps_estimate.toFixed(2)}` : "—"}
										</span>
									</div>
									<div>
										<span className="text-muted-foreground">EPS Act: </span>
										<span
											className={`font-medium ${hasBeat ? (beat ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400") : "text-sky-950 dark:text-sky-50"}`}
										>
											{event.eps_actual != null ? `$${event.eps_actual.toFixed(2)}` : "—"}
										</span>
									</div>
									<div>
										<span className="text-muted-foreground">Rev Est: </span>
										<span className="font-medium text-sky-950 dark:text-sky-50">
											{formatRevenue(event.revenue_estimate)}
										</span>
									</div>
									<div>
										<span className="text-muted-foreground">Rev Act: </span>
										<span className="font-medium text-sky-950 dark:text-sky-50">
											{formatRevenue(event.revenue_actual)}
										</span>
									</div>
								</div>
							</div>
						)
					})}
				</div>
			</DialogContent>
		</Dialog>
	)
}
